import { Link } from "react-router-dom";
import { Crown, Flame, Heart, BookOpen, ArrowRight, Droplets } from "lucide-react";
import { BRAND } from "../lib/api";

const PASTOR_IMG = BRAND.pastorPhoto;

const beliefs = [
  { icon: BookOpen, title: "The Word of God", text: "Scripture is our living foundation — sharper than any sword, alive and active." },
  { icon: Flame, title: "The Fire of God", text: "We welcome the Holy Spirit's purifying presence — for purification, not destruction." },
  { icon: Droplets, title: "The River of Living Water", text: "Out of the surrendered heart flows life — Jesus is the source that never runs dry." },
];

export default function About() {
  return (
    <div data-testid="page-about" className="">
      <section className="container-narrow pt-16 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 reveal reveal-1">
            <div className="label-luxe">About</div>
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mt-4">
              A movement built on <span className="italic gold-text">surrender</span>.
            </h1>
            <p className="mt-7 text-white text-lg leading-relaxed">
              Abundant Life No. 2 — The Surrender Church is an online-first ministry led by
              <span className="text-white font-semibold"> {BRAND.pastor}</span> from {BRAND.city}. We exist to
              help people encounter the living God, break generational cycles, and walk into
              their God-given assignment.
            </p>
            <p className="mt-4 text-white/90 leading-relaxed">
              We are not a religion. We are not a brand. We are a family of surrendered believers
              learning to trust the Holy Spirit one decision at a time.
            </p>
          </div>
          <div className="lg:col-span-5 reveal reveal-2">
            <div className="rounded-[28px] overflow-hidden border border-[#D4AF37]/30 relative shadow-2xl">
              <img src={PASTOR_IMG} alt="Pastor Shalonda" className="w-full aspect-[4/5] object-cover" />
              <div className="absolute inset-0" style={{background:"linear-gradient(180deg, transparent 50%, rgba(11,18,48,0.85) 100%)"}} />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-serif text-2xl text-white">Pastor Shalonda Raymond</div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mt-1">Leader · Speaker · Coach · Visionary</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="stripe-divider" />

      <section className="container-narrow py-24 grid md:grid-cols-2 gap-8">
        <div className="card-luxe">
          <Crown className="w-8 h-8 text-[#ECC23D]" />
          <h2 className="font-serif text-3xl mt-4">Our Mission</h2>
          <p className="mt-4 text-white leading-relaxed">
            To raise up a generation of surrendered believers who walk in healing, clarity, and
            their Kingdom assignment — equipped with the Word, set on fire by the Spirit, and
            poured into by living water.
          </p>
        </div>
        <div className="card-luxe">
          <Heart className="w-8 h-8 text-[#D91668]" />
          <h2 className="font-serif text-3xl mt-4">Our Vision</h2>
          <p className="mt-4 text-white leading-relaxed">
            A global online sanctuary where the broken find healing, the lost find direction, and
            the surrendered find purpose — all flowing from a deep encounter with Jesus Christ.
          </p>
        </div>
      </section>

      <section className="container-narrow py-12">
        <div className="text-center mb-14">
          <div className="label-luxe">Core Beliefs</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-3">The three pillars of <span className="gold-text italic">Abundant Life II</span>.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {beliefs.map((b,i)=>(
            <div key={i} className="card-luxe">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br from-[#D4AF37]/20 to-[#D91668]/15 text-[#ECC23D] mb-5">
                <b.icon className="w-7 h-7" />
              </div>
              <h3 className="font-serif text-2xl">{b.title}</h3>
              <p className="mt-3 text-white/90 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="rounded-[32px] overflow-hidden border border-[#D4AF37]/30 relative card-dark">
          <div className="absolute inset-0 hero-grid opacity-25" />
          <div className="relative grid lg:grid-cols-2 gap-0 items-stretch">
            <div className="p-10 md:p-14 order-2 lg:order-1">
              <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#D4AF37]">My Covering</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-white">
                Grateful for <span className="gold-text italic">vision</span>.<br/>
                Honored for <span className="fuchsia-text italic">covering</span>.
              </h2>
              <div className="mt-6 space-y-5 text-white/90 leading-relaxed">
                <p>
                  I am deeply grateful to God for entrusting me with the vision of <span className="font-semibold text-white">The Surrender Church</span>.
                </p>
                <p>
                  Yet, I am even more thankful for the divine gift of my covering, <span className="font-semibold text-[#ECC23D]">Chief Apostle Dr. Paulette J. Olasoji</span> — the embodiment of vision, strength, and spiritual authority. A woman fully yielded and led by the Holy Spirit.
                </p>
                <p>
                  It is an honor to be taught, guided, and cultivated under such authentic and powerful leadership. I do not take lightly the grace to sit under greatness.
                </p>
                <div className="pt-4 border-t border-white/10">
                  <div className="text-sm uppercase tracking-[0.25em] text-[#D4AF37]">In Her Words</div>
                  <p className="font-serif text-2xl md:text-3xl text-white mt-2 italic">
                    "All I know is what God says."
                  </p>
                </div>
                <div className="text-xs uppercase tracking-[0.3em] text-white/70 pt-2">
                  "Where there is no vision, the people perish." — Proverbs 29:18
                </div>
              </div>
            </div>
            <div className="relative order-1 lg:order-2 min-h-[340px]">
              <img
                src="https://customer-assets.emergentagent.com/job_pastor-shalonda/artifacts/c7g495c2_ChatGPT%20Image%20Apr%2030%2C%202026%2C%2004_09_58%20PM.png"
                alt="Chief Apostle Dr. Paulette J. Olasoji"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 pointer-events-none" style={{background:"linear-gradient(90deg, rgba(0,0,153,0.35) 0%, transparent 30%)"}} />
            </div>
          </div>
        </div>
      </section>

      <section className="container-narrow py-24">
        <div className="rounded-[32px] glass-strong p-10 md:p-16">
          <div className="label-luxe">The Story</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight max-w-3xl">
            From <span className="fuchsia-text italic">private surrender</span> to a public movement.
          </h2>
          <div className="grid md:grid-cols-2 gap-10 mt-10 text-white leading-relaxed">
            <p>
              The Surrender Church was birthed in a quiet moment of prayer — when Pastor Shalonda laid down her own plans and said "yes" to a fresh assignment from the Lord. What began as one woman's surrender has now grown into an online sanctuary, reaching hearts and transforming lives across the country.
            </p>
            <p>
              We chose the name "The Surrender Church" because we believe true abundant life begins at the place of surrender. While the first chapter may belong to the world, the next — the life Jesus promised — belongs to those who lay it all down and follow Him fully.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/services" className="btn-gold">Experience a Service <ArrowRight className="w-4 h-4"/></Link>
            <Link to="/booking" className="btn-ghost-gold">Invite Pastor Shalonda</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
